import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Spinner } from '../ui';

// Mock data - replace with actual API calls
const MOCK_STATS = {
  totalUsers: 1247,
  totalContracts: 3891,
  totalRevenue: 89432.50,
  monthlyGrowth: 12.5,
  activeUsers: 342,
  pendingContracts: 67,
};

const MOCK_RECENT_USERS = [
  { id: 1, name: 'John Smith', email: 'john@example.com', country: 'USA', joinedAt: '2 hours ago', avatar: 'JS' },
  { id: 2, name: 'Marie Dupont', email: 'marie@example.com', country: 'France', joinedAt: '5 hours ago', avatar: 'MD' },
  { id: 3, name: 'Hans Mueller', email: 'hans@example.com', country: 'Germany', joinedAt: '1 day ago', avatar: 'HM' },
  { id: 4, name: 'Emma Wilson', email: 'emma@example.com', country: 'UK', joinedAt: '1 day ago', avatar: 'EW' },
  { id: 5, name: 'Carlos Garcia', email: 'carlos@example.com', country: 'Spain', joinedAt: '2 days ago', avatar: 'CG' },
];

const MOCK_RECENT_CONTRACTS = [
  { id: 'C001', user: 'John Smith', country: 'usa', status: 'paid', amount: 29.99, createdAt: '1 hour ago' },
  { id: 'C002', user: 'Marie Dupont', country: 'france', status: 'pending_payment', amount: 24.99, createdAt: '3 hours ago' },
  { id: 'C003', user: 'Hans Mueller', country: 'germany', status: 'paid', amount: 24.99, createdAt: '5 hours ago' },
  { id: 'C004', user: 'Emma Wilson', country: 'uk', status: 'draft', amount: 29.99, createdAt: '8 hours ago' },
  { id: 'C005', user: 'Carlos Garcia', country: 'spain', status: 'paid', amount: 24.99, createdAt: '1 day ago' },
];

const COUNTRY_DATA = {
  usa: { flag: '🇺🇸', name: 'USA' },
  france: { flag: '🇫🇷', name: 'France' },
  germany: { flag: '🇩🇪', name: 'Germany' },
  uk: { flag: '🇬🇧', name: 'UK' },
  spain: { flag: '🇪🇸', name: 'Spain' },
  italy: { flag: '🇮🇹', name: 'Italy' },
  canada: { flag: '🇨🇦', name: 'Canada' },
  australia: { flag: '🇦🇺', name: 'Australia' },
};

// Stat Card Component
function StatCard({ title, value, icon: Icon, trend, trendUp, color = 'indigo', subtitle }) {
  const colorClasses = {
    indigo: 'bg-indigo-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
  };

  const trendColors = {
    up: 'text-emerald-600 bg-emerald-50',
    down: 'text-rose-600 bg-rose-50',
  };

  return (
    <div className="bg-white rounded-2xl shadow-soft p-6 hover:shadow-lg transition-all duration-300">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
          {trend && (
            <div className={`inline-flex items-center mt-2 px-2 py-1 rounded-full text-xs font-medium ${trendColors[trendUp ? 'up' : 'down']}`}>
              <svg
                className={`w-3 h-3 mr-1 ${trendUp ? '' : 'rotate-180'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              {trend}
            </div>
          )}
        </div>
        <div className={`w-12 h-12 ${colorClasses[color]} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

// Quick Action Card
function QuickActionCard({ title, description, icon: Icon, to, color = 'indigo' }) {
  const colorClasses = {
    indigo: 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700',
    emerald: 'from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700',
    amber: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700',
    purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
  };

  return (
    <Link
      to={to}
      className={`block p-5 rounded-xl bg-gradient-to-br ${colorClasses[color]} text-white transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5`}
    >
      <Icon className="w-8 h-8 mb-3 opacity-90" />
      <h3 className="font-semibold">{title}</h3>
      <p className="text-sm opacity-80 mt-1">{description}</p>
    </Link>
  );
}

// Activity Item
function ActivityItem({ icon, title, description, time, type }) {
  const typeColors = {
    user: 'bg-blue-100 text-blue-600',
    contract: 'bg-emerald-100 text-emerald-600',
    payment: 'bg-amber-100 text-amber-600',
    system: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${typeColors[type]}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 truncate">{description}</p>
      </div>
      <span className="text-xs text-gray-400 whitespace-nowrap">{time}</span>
    </div>
  );
}

// Mini Chart (simple bar chart visualization)
function MiniChart({ data, color = 'indigo' }) {
  const max = Math.max(...data);
  const colorClass = {
    indigo: 'bg-indigo-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
  };

  return (
    <div className="flex items-end gap-1 h-12">
      {data.map((value, index) => (
        <div
          key={index}
          className={`w-2 ${colorClass[color]} rounded-t opacity-60 hover:opacity-100 transition-opacity`}
          style={{ height: `${(value / max) * 100}%` }}
        />
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(MOCK_STATS);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentContracts, setRecentContracts] = useState([]);

  useEffect(() => {
    // Simulate API loading
    setTimeout(() => {
      setRecentUsers(MOCK_RECENT_USERS);
      setRecentContracts(MOCK_RECENT_CONTRACTS);
      setLoading(false);
    }, 500);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" label="Loading dashboard..." />
      </div>
    );
  }

  // Sample chart data
  const chartData = [45, 52, 38, 65, 48, 72, 58, 82, 75, 90, 85, 95];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex items-center gap-3">
          <select className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option>Last 7 days</option>
            <option>Last 30 days</option>
            <option>Last 90 days</option>
            <option>This year</option>
          </select>
          <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers.toLocaleString()}
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          )}
          trend="+12.5%"
          trendUp={true}
          color="indigo"
        />
        <StatCard
          title="Total Contracts"
          value={stats.totalContracts.toLocaleString()}
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
          trend="+8.2%"
          trendUp={true}
          color="emerald"
        />
        <StatCard
          title="Total Revenue"
          value={`$${stats.totalRevenue.toLocaleString()}`}
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          trend="+23.1%"
          trendUp={true}
          color="amber"
        />
        <StatCard
          title="Pending Contracts"
          value={stats.pendingContracts}
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          subtitle="Requires attention"
          color="rose"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Revenue Overview</h3>
              <p className="text-sm text-gray-500">Monthly revenue for the past year</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-indigo-500 rounded-full"></span>
                <span className="text-sm text-gray-600">Revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                <span className="text-sm text-gray-600">Contracts</span>
              </div>
            </div>
          </div>
          {/* Chart placeholder - would use a real chart library */}
          <div className="h-64 flex items-end justify-between gap-2 px-4">
            {chartData.map((value, index) => (
              <div key={index} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-gradient-to-t from-indigo-500 to-indigo-400 rounded-t-lg transition-all hover:from-indigo-600 hover:to-indigo-500"
                  style={{ height: `${(value / 100) * 200}px` }}
                />
                <span className="text-xs text-gray-400">
                  {['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][index]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Country Distribution */}
        <div className="bg-white rounded-2xl shadow-soft p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h3>
          <div className="space-y-4">
            {[
              { country: 'usa', percentage: 35, count: 1362 },
              { country: 'uk', percentage: 22, count: 857 },
              { country: 'germany', percentage: 18, count: 700 },
              { country: 'france', percentage: 15, count: 584 },
              { country: 'spain', percentage: 10, count: 388 },
            ].map((item) => (
              <div key={item.country} className="flex items-center gap-3">
                <span className="text-xl">{COUNTRY_DATA[item.country]?.flag}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">
                      {COUNTRY_DATA[item.country]?.name}
                    </span>
                    <span className="text-sm text-gray-500">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 rounded-full transition-all"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickActionCard
          title="Add User"
          description="Create new account"
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          )}
          to="/admin/users/new"
          color="indigo"
        />
        <QuickActionCard
          title="View Reports"
          description="Analytics & insights"
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
          to="/admin/analytics"
          color="emerald"
        />
        <QuickActionCard
          title="Manage Templates"
          description="Contract templates"
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
            </svg>
          )}
          to="/admin/templates"
          color="amber"
        />
        <QuickActionCard
          title="Settings"
          description="System configuration"
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
          to="/admin/settings"
          color="purple"
        />
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Users</h3>
            <Link to="/admin/users" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentUsers.map((user) => (
              <div key={user.id} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">{user.avatar}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">{user.country}</p>
                  <p className="text-xs text-gray-500">{user.joinedAt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Contracts */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Contracts</h3>
            <Link to="/admin/contracts" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {recentContracts.map((contract) => (
              <div key={contract.id} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                <span className="text-xl">{COUNTRY_DATA[contract.country]?.flag}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{contract.user}</p>
                  <p className="text-xs text-gray-500">{contract.id}</p>
                </div>
                <Badge.Status status={contract.status} />
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">${contract.amount}</p>
                  <p className="text-xs text-gray-500">{contract.createdAt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
